/**
 * Klient til core-modulet (/api/auth/*, /api/health).
 *
 * Kontrakt 0.11.0 (core 0.4.0): `POST /api/auth/login` tager `{username, password}`.
 * Brugernavnet (paakraevet, trimmet, uafhaengigt af store/smaa bogstaver) slaas op i
 * core's brugerregister og bliver JWT'ens `sub`; det fulde navn kommer med som claimet
 * `name` og er "hvem" i master's aendringshistorik.
 */
import { api } from './api';

export interface LoginResponse {
  token: string;
  expires_at: string;
}

export interface MeResponse {
  /** JWT'ens `sub`: brugernavnet fra login. */
  subject: string;
  /** Det fulde navn fra core's brugerregister (fallback: brugernavnet). */
  name: string;
  roles: string[];
  expires_at: string;
}

/** Maks laengde paa brugernavnet i login (kontrakt 0.11.0). */
export const LOGIN_USERNAME_MAX_LENGTH = 80;

/** localStorage-noeglen, der husker brugernavnet mellem logins. */
export const LOGIN_USERNAME_KEY = 'hbc.loginUsername';

/** Den gamle noegle fra kontrakt 0.3.0; ryddes ved foerste laesning. */
const LEGACY_LOGIN_NAME_KEY = 'hbc.loginName';

/** Det huskede brugernavn fra sidste login ("" naar der ikke er et). */
export function readRememberedUsername(): string {
  try {
    // Det gamle "Dit navn" er ikke et brugernavn og maa ikke blive haengende.
    localStorage.removeItem(LEGACY_LOGIN_NAME_KEY);
    return localStorage.getItem(LOGIN_USERNAME_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Husker (eller glemmer) brugernavnet til naeste login. */
export function rememberUsername(username: string): void {
  const trimmed = username.trim().slice(0, LOGIN_USERNAME_MAX_LENGTH);
  try {
    if (trimmed === '') localStorage.removeItem(LOGIN_USERNAME_KEY);
    else localStorage.setItem(LOGIN_USERNAME_KEY, trimmed);
  } catch {
    /* privat browsertilstand: vi koerer videre uden persistering */
  }
}

/**
 * POST /api/auth/login – body `{username, password}` (kontrakt 0.11.0).
 * Fejl vises af kalderen (silent).
 */
export function login(username: string, password: string): Promise<LoginResponse> {
  const trimmed = username.trim().slice(0, LOGIN_USERNAME_MAX_LENGTH);
  return api.post<LoginResponse>(
    '/api/auth/login',
    { username: trimmed, password },
    { anonymous: true, silent: true },
  );
}

export function me(): Promise<MeResponse> {
  return api.get<MeResponse>('/api/auth/me', { silent: true });
}

/* ------------------------------------------------- Microsoft-login (0.21.0) */

/** Svar fra GET /api/auth/providers (kontrakt 0.21.0). */
export interface ProvidersResponse {
  microsoft: boolean;
}

/** Gatewaystien, der svarer 302 til Microsoft (hele siden skifter, ikke et fetch). */
export const MICROSOFT_LOGIN_PATH = '/api/auth/microsoft/login';

/** GET /api/auth/providers – uden Authorization. Fejl håndteres af kalderen. */
export function getProviders(signal?: AbortSignal): Promise<ProvidersResponse> {
  return api.get<ProvidersResponse>('/api/auth/providers', {
    anonymous: true,
    silent: true,
    signal,
  });
}

/**
 * POST /api/auth/microsoft/token – uden Bearer; core læser handoff-cookien (engangsbrug,
 * 60 s) og svarer som /login. Et 401 her er "Microsoft-login er udløbet", ikke en
 * udløbet session, og må ikke trigge den globale 401-handler.
 */
export function microsoftToken(): Promise<LoginResponse> {
  return api.post<LoginResponse>('/api/auth/microsoft/token', undefined, {
    anonymous: true,
    silent: true,
    skipUnauthorizedHandler: true,
  });
}

/** Fejltekst, når handoff-cookien mangler, er udløbet eller er brugt (401). */
export const MICROSOFT_EXPIRED_TEXT = 'Microsoft-login er udløbet. Prøv igen.';

const MICROSOFT_LOGIN_ERRORS: Record<string, string> = {
  config: 'Microsoft-login er ikke sat op.',
  state: 'Login-forsøget er udløbet. Prøv igen.',
  afvist: 'Login blev afbrudt hos Microsoft.',
  token: 'Microsoft-login mislykkedes. Prøv igen.',
};

/** Dansk tekst til `/login?login_error=<kode>`; en ukendt kode giver teksten for `token`. */
export function microsoftLoginErrorText(code: string): string {
  return Object.prototype.hasOwnProperty.call(MICROSOFT_LOGIN_ERRORS, code)
    ? MICROSOFT_LOGIN_ERRORS[code]
    : MICROSOFT_LOGIN_ERRORS.token;
}
